import { Fragment, type ButtonHTMLAttributes, type HTMLAttributes, type ReactNode } from 'react';

import { cn } from '../../../lib/utils.js';

export type QuickPanelDrawerSize = 'sm' | 'md' | 'lg' | 'xl';

export interface QuickPanelStackProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

export function QuickPanelStack({ className, children, ...props }: QuickPanelStackProps) {
  return (
    <div
      className={cn('qp-stack', className)}
      role="presentation"
      aria-label="Open quick panels"
      {...props}
    >
      {children}
    </div>
  );
}

export interface QuickPanelDrawerProps extends HTMLAttributes<HTMLElement> {
  size?: QuickPanelDrawerSize;
  pinned?: boolean;
  /** Stack position for z-index layering when multiple drawers are open. */
  stackIndex?: number;
  children: ReactNode;
}

export function QuickPanelDrawer({
  size = 'lg',
  pinned = false,
  stackIndex,
  className,
  children,
  ...props
}: QuickPanelDrawerProps) {
  return (
    <aside
      className={cn(
        'qp-drawer',
        `qp-drawer--${size}`,
        pinned && 'is-pinned',
        stackIndex !== undefined && `qp-drawer--stack-${stackIndex}`,
        className,
      )}
      {...props}
    >
      {children}
    </aside>
  );
}

export interface QuickPanelDrawerHeaderProps extends HTMLAttributes<HTMLElement> {
  children: ReactNode;
}

export function QuickPanelDrawerHeader({
  className,
  children,
  ...props
}: QuickPanelDrawerHeaderProps) {
  return (
    <header className={cn('qp-drawer-header', className)} {...props}>
      {children}
    </header>
  );
}

export interface QuickPanelDrawerIconProps {
  children: ReactNode;
  className?: string;
}

export function QuickPanelDrawerIcon({ children, className }: QuickPanelDrawerIconProps) {
  return <span className={cn('qp-icon', className)}>{children}</span>;
}

export interface QuickPanelDrawerTitleProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

export function QuickPanelDrawerTitle({
  className,
  children,
  ...props
}: QuickPanelDrawerTitleProps) {
  return (
    <div className={cn('qp-title', className)} {...props}>
      {children}
    </div>
  );
}

export interface QuickPanelDrawerActionsProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

export function QuickPanelDrawerActions({
  className,
  children,
  ...props
}: QuickPanelDrawerActionsProps) {
  return (
    <div className={cn('qp-actions', className)} {...props}>
      {children}
    </div>
  );
}

export interface QuickPanelDrawerBodyProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

export function QuickPanelDrawerBody({ className, children, ...props }: QuickPanelDrawerBodyProps) {
  return (
    <div className={cn('qp-drawer-body', className)} {...props}>
      {children}
    </div>
  );
}

export interface QuickPanelDrawerFooterProps extends HTMLAttributes<HTMLElement> {
  children: ReactNode;
}

export function QuickPanelDrawerFooter({
  className,
  children,
  ...props
}: QuickPanelDrawerFooterProps) {
  return (
    <footer className={cn('qp-drawer-footer', className)} {...props}>
      {children}
    </footer>
  );
}

export interface QuickPanelTrailProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

export function QuickPanelTrail({ className, children, ...props }: QuickPanelTrailProps) {
  return (
    <div className={cn('qp-trail', className)} {...props}>
      {children}
    </div>
  );
}

export interface QuickPanelTrailCrumbProps extends HTMLAttributes<HTMLSpanElement> {
  children: ReactNode;
  current?: boolean;
}

export function QuickPanelTrailCrumb({
  current = false,
  className,
  children,
  ...props
}: QuickPanelTrailCrumbProps) {
  return (
    <span className={cn('crumb', current && 'current', className)} {...props}>
      {children}
    </span>
  );
}

export interface QuickPanelTrailSeparatorProps extends HTMLAttributes<HTMLSpanElement> {
  children?: ReactNode;
}

export function QuickPanelTrailSeparator({
  className,
  children = '›',
  ...props
}: QuickPanelTrailSeparatorProps) {
  return (
    <span className={cn('sep', className)} {...props}>
      {children}
    </span>
  );
}

export interface QuickPanelTrailCrumbsProps {
  crumbs: string[];
}

export function QuickPanelTrailCrumbs({ crumbs }: QuickPanelTrailCrumbsProps) {
  if (crumbs.length === 0) {
    return null;
  }

  return (
    <QuickPanelTrail>
      {crumbs.map((crumb, trailIndex) => (
        <Fragment key={`${crumb}-${trailIndex}`}>
          {trailIndex > 0 ? <QuickPanelTrailSeparator /> : null}
          <QuickPanelTrailCrumb current={trailIndex === crumbs.length - 1}>
            {crumb}
          </QuickPanelTrailCrumb>
        </Fragment>
      ))}
    </QuickPanelTrail>
  );
}

export interface QuickPanelDrawerMainProps {
  trail?: ReactNode;
  children: ReactNode;
}

/** Canonical drawer content shell — trail (breadcrumb) + scrollable body. */
export function QuickPanelDrawerMain({ trail, children }: QuickPanelDrawerMainProps) {
  return (
    <div className="qp-drawer-main">
      {trail}
      <QuickPanelDrawerBody>{children}</QuickPanelDrawerBody>
    </div>
  );
}

export interface QuickPanelDockProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

export function QuickPanelDock({ className, children, ...props }: QuickPanelDockProps) {
  return (
    <div className={cn('qp-dock', className)} role="toolbar" aria-label="Quick panels" {...props}>
      {children}
    </div>
  );
}

export type QuickPanelDockSeparatorProps = HTMLAttributes<HTMLDivElement>;

export function QuickPanelDockSeparator({ className, ...props }: QuickPanelDockSeparatorProps) {
  return <div className={cn('qp-dock-sep', className)} {...props} />;
}

export interface QuickPanelDockButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  open?: boolean;
  children: ReactNode;
}

export function QuickPanelDockButton({
  open = false,
  className,
  type = 'button',
  children,
  ...props
}: QuickPanelDockButtonProps) {
  return (
    <button type={type} className={cn(open && 'is-open', className)} aria-pressed={open} {...props}>
      {children}
    </button>
  );
}

export interface QuickPanelDockBadgeProps extends HTMLAttributes<HTMLSpanElement> {
  children: ReactNode;
}

export function QuickPanelDockBadge({ className, children, ...props }: QuickPanelDockBadgeProps) {
  return (
    <span className={cn('qp-badge', className)} {...props}>
      {children}
    </span>
  );
}
